import { useEffect, useState } from "preact/hooks";
import { deleteCharacter, getCharacter, saveCharacter } from "@/lib/api";
import { emptyCharacter } from "./draft";
import {
  ColorField,
  EmojiField,
  Field,
  TextArea,
  TextInput,
  ToastBanner,
  ButtonGroup,
} from "./ui";
import { navigate } from "@/router";

export function CharacterEditor({ id }: { id: string }) {
  const [draft, setDraft] = useState(emptyCharacter());
  const [loaded, setLoaded] = useState(!id);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{
    kind: "error" | "ok" | "warn";
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void getCharacter(id).then((row) => {
      if (cancelled || !row) return;
      setDraft({
        id,
        title: row.title,
        role: row.role,
        blurb: row.blurb,
        avatarEmoji: row.avatar_emoji,
        color: row.color,
        stats: row.stats ?? [],
        traits: row.traits ?? [],
        tags: row.tags ?? [],
        public: row.public,
      });
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const patch = (updates: Partial<ReturnType<typeof emptyCharacter>>): void => {
    setDraft((d) => ({ ...d, ...updates }));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    const { row, error } = await saveCharacter(draft);
    setSaving(false);
    if (error) {
      setBanner({ kind: "error", message: error });
      return;
    }
    setBanner({ kind: "ok", message: "Character saved" });
    if (row && !draft.id) navigate(`/edit/character/${row.id}`);
  };

  const remove = async (): Promise<void> => {
    if (!draft.id) return;
    if (!window.confirm(`Delete character "${draft.title}"?`)) return;
    const error = await deleteCharacter(draft.id);
    if (error) setBanner({ kind: "error", message: error });
    else navigate("/");
  };

  if (!loaded) {
    return <p className="muted">Loading character…</p>;
  }

  return (
    <div className="editor-page">
      <header className="editor-header">
        <h2 className="hub-logo">Character studio</h2>
        <ButtonGroup>
          <button className="choice-button" onClick={() => navigate("/")}>
            ← Hub
          </button>
          <button
            className="choice-button primary"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save character"}
          </button>
          {draft.id ? (
            <button
              className="choice-button danger"
              onClick={() => void remove()}
            >
              Delete
            </button>
          ) : null}
        </ButtonGroup>
      </header>

      <ToastBanner
        kind={banner?.kind ?? "ok"}
        message={banner?.message ?? null}
      />

      <div className="editor-grid">
        <section className="panel">
          <h3>Identity</h3>
          <Field label="Name">
            <TextInput
              value={draft.title}
              onChange={(v) => patch({ title: v })}
            />
          </Field>
          <Field label="Role">
            <TextInput
              value={draft.role}
              onChange={(v) => patch({ role: v })}
            />
          </Field>
          <Field
            label="Colour"
            hint="Drive the game's atmosphere when this character takes the stage."
          >
            <span
              className="swatch-preview"
              style={{ background: draft.color }}
            >
              {draft.avatarEmoji}
            </span>
            <ColorField
              value={draft.color}
              onChange={(color) => patch({ color })}
            />
          </Field>
          <Field label="Avatar">
            <EmojiField
              value={draft.avatarEmoji}
              onChange={(avatarEmoji) => patch({ avatarEmoji })}
            />
          </Field>
          <Field label="Blurb">
            <TextArea
              value={draft.blurb}
              onChange={(v) => patch({ blurb: v })}
              rows={3}
            />
          </Field>
          <label className="field row">
            <span className="field-label">Share in the public library</span>
            <input
              type="checkbox"
              checked={draft.public}
              onChange={(e) => patch({ public: e.currentTarget.checked })}
            />
          </label>
        </section>

        <section className="panel">
          <h3>Stats</h3>
          <p className="muted">
            Free-form attributes any story can riff on (e.g. Level · Wit ·
            Buginess).
          </p>
          {draft.stats.map((stat, i) => (
            <div className="row-pair" key={i}>
              <TextInput
                value={stat.label}
                placeholder="label"
                onChange={(v) => setStat(i, { ...stat, label: v })}
              />
              <TextInput
                value={stat.value}
                placeholder="value"
                onChange={(v) => setStat(i, { ...stat, value: v })}
              />
              <button
                className="icon-btn"
                title="Remove stat"
                onClick={() => removeStat(i)}
              >
                ×
              </button>
            </div>
          ))}
          <button className="choice-button" onClick={addStat}>
            + Add stat
          </button>

          <h3>Traits</h3>
          <p className="muted">
            Short keywords (e.g. "impatient", "loves chrome").
          </p>
          {draft.traits.map((trait, i) => (
            <div className="row-pair" key={i}>
              <TextInput value={trait} onChange={(v) => setTrait(i, v)} />
              <button
                className="icon-btn"
                title="Remove trait"
                onClick={() => removeTrait(i)}
              >
                ×
              </button>
            </div>
          ))}
          <button className="choice-button" onClick={addTrait}>
            + Add trait
          </button>

          <h3>Tags</h3>
          <Field label="Comma-separated tags">
            <TextInput
              value={draft.tags.join(", ")}
              onChange={(v) =>
                patch({
                  tags: v
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
                })
              }
            />
          </Field>
        </section>
      </div>
    </div>
  );

  function setStat(index: number, stat: (typeof draft.stats)[number]): void {
    patch({ stats: draft.stats.map((s, i) => (i === index ? stat : s)) });
  }
  function removeStat(index: number): void {
    patch({ stats: draft.stats.filter((_, i) => i !== index) });
  }
  function addStat(): void {
    patch({ stats: [...draft.stats, { label: "", value: "" }] });
  }
  function setTrait(index: number, value: string): void {
    patch({ traits: draft.traits.map((t, i) => (i === index ? value : t)) });
  }
  function removeTrait(index: number): void {
    patch({ traits: draft.traits.filter((_, i) => i !== index) });
  }
  function addTrait(): void {
    patch({ traits: [...draft.traits, ""] });
  }
}
